import { GetItemsAsyncArgs } from '@/network/useNetwork';
import { QueryPrefix } from '@/queryClient';
import { omitNil } from './omitNil';

type RecordOfStrings = Record<string, string[]>;

type QueryKeyScope = 'list' | 'detail';

type QueryKey = [QueryPrefix, { scope: QueryKeyScope }] | [QueryPrefix];

/**
 * QueryParams represents optional includes and filters commonly used in list queries.
 * - includes: array of relationship/include strings supported by the backend
 * - filters: key/value pairs used to filter results
 */
export type QueryParams<TIncludes extends string, TFilters extends Record<string, unknown>> = {
  includes?: TIncludes[];
  filters?: Partial<TFilters>;
};

/**
 * DefaultPayload describes the base payload for a query, typically stable across a hook.
 * Combine this with per-call overrides to produce the final payload.
 */
export type DefaultPayload<
  TIncludes extends string,
  TFilters extends Record<string, unknown>,
> = Pick<GetItemsAsyncArgs, 'type' | 'limit' | 'offset' | 'order' | 'direction'> &
  QueryParams<TIncludes, TFilters>;

export type Payload<
  TIncludes extends string | never,
  TFilters extends Record<string, unknown>,
> = Omit<GetItemsAsyncArgs, 'type' | 'filters' | 'includes'> & QueryParams<TIncludes, TFilters>;

type CreateQueryArgs<TIncludes extends string, TFilters extends Record<string, unknown>> = {
  prefix: QueryPrefix;
  defaultPayload: DefaultPayload<TIncludes, TFilters>;
};

// Canonical deep stringify to eliminate key order sensitivity in nested objects
const stableSerialize = (value: unknown): string => {
  if (value === null || typeof value !== 'object') return String(value);
  if (Array.isArray(value)) {
    // Normalize each element then sort for order insensitivity
    return `[${value.map(stableSerialize).sort().join(',')}]`;
  }
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableSerialize(obj[k])}`).join(',')}}`;
};

const reduceKeyVal = (entries: [string, unknown][]) => {
  const temp: RecordOfStrings = {};
  for (const [key, value] of entries) {
    if (value === undefined) {
      continue;
    }

    const bucket = temp[key] || [];
    if (Array.isArray(value)) {
      const mapped = value.map((v) =>
        typeof v === 'object' && v !== null ? stableSerialize(v) : String(v)
      );
      temp[key] = bucket.concat(mapped);
    } else if (typeof value === 'object' && value !== null) {
      temp[key] = bucket.concat(stableSerialize(value));
    } else {
      temp[key] = bucket.concat(String(value));
    }
  }

  const sorted: RecordOfStrings = {};
  Object.keys(temp)
    .sort()
    .forEach((k) => {
      const unique = Array.from(new Set(temp[k]));
      unique.sort();
      sorted[k] = unique;
    });

  return sorted;
};

/**
 * Builds a stable TanStack Query key. **There cannot be a key with params but without scope.**
 * @param {QueryPrefix} prefix prefix used as the base for the queryKey
 * @param {'list' | 'detail'} scope 'list' or 'detail'. If omitted, key is just [prefix].
 * @param {object} params any payload object. Nested objects/arrays are normalized and sorted for
 *   order-insensitive stability.
 *
 * @example
 * const key = createQueryKeyFromPayload({
 *   prefix: 'assignments',
 *   scope: 'list',
 *   params: { type: ItemType.Assignment, filters: { courseId, sectionId } },
 * });
 */
const createQueryKeyFromPayload = <T extends Partial<Record<keyof T, unknown>>>({
  prefix,
  scope,
  params,
}: {
  prefix: QueryPrefix;
  scope?: QueryKeyScope;
  params?: T;
}): QueryKey => {
  if (!scope) {
    return [prefix];
  }

  const normalizedParams = params ? reduceKeyVal(Object.entries(params)) : {};
  return [
    prefix,
    {
      scope,
      ...normalizedParams,
    },
  ];
};

/**
 * Creates a set of TanStack Query key helpers scoped to a specific prefix.
 *
 * Each helper produces a stable `QueryKey` by normalizing inputs and
 * namespacing them under the provided prefix.
 *
 * @param {QueryPrefix} prefix - Namespace prefix for all generated query keys.
 *
 * @returns An object of query key helper functions.
 *
 * @returns.all
 * Returns the root query key.
 * ```ts
 * [prefix]
 * ```
 *
 * @returns.lists
 * Returns the base key for all list queries.
 * ```ts
 * [prefix, { scope: 'list' }]
 * ```
 *
 * @returns.list
 * Returns a list query key with normalized filter parameters.
 *
 * @typeParam T - Shape of the filters/params object.
 * @param filters - Parameters used to scope the list query.
 *
 * ```ts
 * [prefix, { scope: 'list', ...normalizedFilters }]
 * ```
 *
 * @returns.details
 * Returns the base key for all detail queries.
 * ```ts
 * [prefix, { scope: 'detail' }]
 * ```
 *
 * @returns.detail
 * Returns a detail query key scoped to a specific entity id.
 *
 * @param id - Identifier for the detail query. If undefined, an empty string
 * is used to preserve key stability.
 *
 * ```ts
 * [prefix, { scope: 'detail', id }]
 * ```
 *
 * @example
 * ```ts
 * const { getPayload, queryKeys } = getQueryPayloadAndKey({
 *   prefix,
 *   defaultPayload,
 * });
 *
 * const payload = getPayload({ filters: { courseId } });
 *
 * useQuery({
 *   queryKey: queryKeys.list(payload),
 *   queryFn: fetchItems,
 * });
 * ```
 */
export const createQueryKeys = (prefix: QueryPrefix) => {
  const all = (): QueryKey => createQueryKeyFromPayload({ prefix });
  const lists = (): QueryKey => createQueryKeyFromPayload({ prefix, scope: 'list' });
  const list = <T extends Partial<Record<keyof T, unknown>> = GetItemsAsyncArgs>(
    filters: T
  ): QueryKey => createQueryKeyFromPayload({ prefix, scope: 'list', params: filters });
  const details = (): QueryKey => createQueryKeyFromPayload({ prefix, scope: 'detail' });
  const detail = (id: string | number | undefined): QueryKey =>
    createQueryKeyFromPayload({ prefix, scope: 'detail', params: { id: id ?? '' } });
  return { all, lists, list, details, detail } as const;
};

/**
 * Constructs helpers for building a stable query payload and query keys
 * for use with TanStack Query.
 *
 * @template TIncludes - Union of allowed include string literals.
 * @template TFilters - Shape of the filters object.
 *
 * @param args - Configuration for query payloads and keys.
 * @param args.prefix - Prefix used to namespace query keys.
 * @param args.defaultPayload - Base payload applied to all queries.
 *
 * @returns An object containing payload and query key helpers.
 *
 * @returns.getPayload
 * A function that merges the `defaultPayload` with optional per-call overrides.
 * - `includes` from defaults and overrides are merged and de-duplicated.
 * - `filters` are shallow-merged and cleaned using `omitNil`.
 *
 * @returns.queryKeys
 * A set of helpers for building stable list and detail query keys,
 * created via `createQueryKeys(prefix)`.
 *
 * @example
 * ```ts
 * const { getPayload, queryKeys } = getQueryPayloadAndKey({
 *   prefix: 'assignments',
 *   defaultPayload: {
 *     type: ItemType.Assignment,
 *     limit: 50,
 *     offset: 0,
 *     order: 'updatedAt',
 *     direction: 'desc',
 *     includes: ['course', 'section'],
 *     filters: { courseId },
 *   },
 * });
 *
 * const payload = getPayload({ filters: { sectionId } });
 *
 * return useQuery({
 *   queryKey: queryKeys.list(payload),
 *   queryFn: ({ signal }) => getItemsAsync(payload, signal),
 * });
 * ```
 */
export const getQueryPayloadAndKey = <
  TIncludes extends string | never,
  TFilters extends Record<string, unknown>,
>(
  args: CreateQueryArgs<TIncludes, TFilters>
) => {
  const { prefix, defaultPayload } = args;

  const getPayload = (payload?: Payload<TIncludes, TFilters>) => ({
    ...defaultPayload,
    ...payload,
    includes: Array.from(
      new Set([...(defaultPayload.includes ?? []), ...(payload?.includes ?? [])])
    ),
    filters: omitNil({
      ...defaultPayload.filters,
      ...payload?.filters,
    }),
  });

  const queryKeys = createQueryKeys(prefix);

  return { getPayload, queryKeys };
};
